/** Requirements, positions and what may fill one. The kind is read off the type name. */

import { REQUIREMENT_CATALOG, type RequirementKind, type StoreRollup } from "@nebulize/sdk";

import type {
  AddedActivityRow,
  BindingTargetKind,
  CatalogEntry,
  ConsoleBinding,
  NamedRequirement,
  Requirement,
} from "../types";

import { providesOf } from "./catalog";
import { isLive, runStatusOf } from "./runState";

export type { RequirementKind };

/** The kinds the platform recognises, off the generated table rather than a list kept here. */
export const REQUIREMENT_KINDS: readonly RequirementKind[] = Object.keys(
  REQUIREMENT_CATALOG.target_kinds_by_kind ?? {},
).sort() as RequirementKind[];

/** The default a name with no recognised prefix reads as. */
const DEFAULT_KIND: RequirementKind = "interface";

const REGISTERED = new Map(
  (REQUIREMENT_CATALOG.types ?? []).map((one) => [one.name, one] as const),
);

/** Whether serving this type needs a paired machine. `REQUIREMENT_CATALOG.types[].local`. */
export function needsMachine(type: string): boolean {
  return REGISTERED.get(type)?.local === true;
}

/** Every registered type that needs a machine, off `REQUIREMENT_CATALOG` rather than a local list. */
export const MACHINE_REQUIREMENT_TYPES: readonly string[] = (REQUIREMENT_CATALOG.types ?? [])
  .filter((one) => one.local === true)
  .map((one) => one.name);

/** The kind, registered where the table knows the name and read off the prefix where it does not. */
export function requirementKind(type: string): RequirementKind {
  const registered = REGISTERED.get(type);
  if (registered !== undefined) return registered.kind;
  const prefix = type.split(".")[0] ?? "";
  const found = REQUIREMENT_KINDS.find((kind) => kind === prefix && kind !== DEFAULT_KIND);
  return found ?? DEFAULT_KIND;
}

/** Two type names wire on exact equality, major version included. `manifest.wires`. */
export function wires(required: string, provided: string): boolean {
  return required === provided;
}

/** Which target kinds may fill this requirement. `REQUIREMENT_CATALOG` answers, never this file. */
export function legalTargetKinds(type: string): BindingTargetKind[] {
  const registered = REGISTERED.get(type);
  if (registered !== undefined) return [...(registered.target_kinds ?? [])];
  return [...(REQUIREMENT_CATALOG.target_kinds_by_kind?.[requirementKind(type)] ?? [])];
}

/** How a person names one target kind. The console's one vocabulary for what fills a position. */
export const TARGET_KIND_WORDS: Record<BindingTargetKind, string> = {
  machine: "a paired machine",
  device: "a paired canvas",
  person: "a member of this space",
  activity: "an activity on this space",
  directory: "a granted directory",
  feed: "a feed this account holds",
};

/** What fills this requirement instead of a binding, or null when a binding does. */
export function servedInstead(type: string): string | null {
  if (legalTargetKinds(type).length > 0) return null;
  if (requirementKind(type) === "interface") {
    return "an added activity providing it, wired when this starts";
  }
  return "a feed attached to this space";
}

/** What fills one position, in a person's words, for whichever kind this type is. */
export function fillingLabel(type: string): string {
  const kinds = legalTargetKinds(type);
  if (kinds.length === 0) return `filled by ${servedInstead(type) ?? ""}`;
  return `filled by ${kinds.map((kind) => TARGET_KIND_WORDS[kind]).join(" or ")}`;
}

/** One position a store card may name. */
export interface RequiredPosition {
  id: string;
  requirement: string;
  activity_id: string;
  min: number;
  max: number;
}

/** Every position this activity requires, of every kind. `StoreRollup.position_bounds`. */
export function requiredPositionsOf(rollup: StoreRollup | undefined): RequiredPosition[] {
  return (rollup?.position_bounds ?? []).map((one) => ({
    id: `${one.activity_id}:${one.requirement}`,
    requirement: one.requirement,
    activity_id: one.activity_id,
    min: one.min,
    max: one.max,
  }));
}

/** How many of one position, as a phrase. */
export function boundLabel(min: number, max: number): string {
  if (min !== max) return `${String(min)} to ${String(max)}`;
  return min === 1 ? "exactly one" : `exactly ${String(min)}`;
}

/** One added activity that could serve an interface requirement, carried by its own name. */
export interface ProviderCandidate {
  added_activity_id: string;
  name: string;
  activity_id: string;
  /** Whether it is up now. A stopped one is still bindable. */
  running: boolean;
}

/** One added activity as a candidate: its name, and what its mount says. */
export function candidateOf(row: AddedActivityRow): ProviderCandidate {
  return {
    added_activity_id: row.added_activity_id,
    name: row.name,
    activity_id: row.activity_id,
    running: isLive(runStatusOf(row.mount)),
  };
}

/** Every added activity whose pinned manifest provides this exact interface, itself excluded. */
export function interfaceProviders(
  type: string,
  rows: readonly AddedActivityRow[],
  entryFor: (row: AddedActivityRow) => CatalogEntry | undefined,
  asking: string,
): ProviderCandidate[] {
  const out: ProviderCandidate[] = [];
  for (const row of rows) {
    if (row.added_activity_id === asking) continue;
    const entry = entryFor(row);
    if (entry === undefined) continue;
    if (!providesOf(entry).some((provided) => wires(type, provided))) continue;
    out.push(candidateOf(row));
  }
  return out;
}

/** What one interface position reads as. `provider_removed` reads as unwired to a person. */
export type InterfaceWiringState =
  | "unwired"
  | "provider_removed"
  | "running"
  | "not_running"
  | "unreadable";

export interface InterfaceWiring {
  state: InterfaceWiringState;
  /** True when the platform wired it at add time rather than a person choosing it. */
  automatic: boolean;
  /** The added activity the binding names, while it is still on this space. */
  provider: ProviderCandidate | null;
  /** The one action that clears this state, empty when nothing is owed. */
  remedy: string;
}

/** One interface position, read off its binding. `liveRead` false means no mount read landed. */
export function interfaceWiring(
  binding: ConsoleBinding | null,
  rows: readonly AddedActivityRow[],
  providers: readonly ProviderCandidate[],
  liveRead: boolean,
): InterfaceWiring {
  if (binding === null) {
    return {
      state: "unwired",
      automatic: false,
      provider: null,
      remedy:
        providers.length === 0
          ? "add an activity that provides it, then choose it here"
          : "choose which added activity serves it",
    };
  }

  const automatic = binding.origin === "auto_wired";
  const named = binding.provider_added_activity_id ?? null;
  const row = rows.find((one) => one.added_activity_id === named);
  if (named === null || row === undefined) {
    return {
      state: "provider_removed",
      automatic,
      provider: null,
      remedy:
        providers.length === 0
          ? "the activity that served it was removed: add another that provides it"
          : "the activity that served it was removed: choose another",
    };
  }

  const provider = candidateOf(row);
  if (!liveRead) {
    return {
      state: "unreadable",
      automatic,
      provider,
      remedy: "the live read failed, so whether it is running is unavailable",
    };
  }
  if (!provider.running) {
    return {
      state: "not_running",
      automatic,
      provider,
      remedy: `start ${provider.name}, or choose one that is running`,
    };
  }
  return { state: "running", automatic, provider, remedy: "" };
}

/** What one interface position reads as, in a person's words. */
export function wiringLabel(wiring: InterfaceWiring): string {
  const named = wiring.provider?.name ?? "";
  switch (wiring.state) {
    case "unwired":
      return "nothing bound";
    case "provider_removed":
      return "nothing bound: the activity that served it was removed";
    case "unreadable":
      return `${named} is bound, and whether it is running is unavailable`;
    case "not_running":
      return `${named} is bound, and is not running`;
    default:
      return `${named} is bound, and is running`;
  }
}

/** How this position came to name what it names. */
export function originLabel(binding: ConsoleBinding | null): string {
  if (binding === null) return "";
  return binding.origin === "auto_wired" ? "wired automatically" : "chosen here";
}

/** How many positions this requirement resolves to, after the setting its count reads. */
export function countOf(added: AddedActivityRow, requirement: Requirement): number {
  const count = requirement.count ?? 1;
  if (typeof count === "number") return count;
  if (count.from == null) return count.max;
  const driven = added.settings[count.from];
  return typeof driven === "number" ? driven : count.min;
}

/** Cardinality as a phrase. */
export function cardinalityLabel(count: Requirement["count"]): string {
  const value = count ?? 1;
  if (typeof value === "number") return value === 1 ? "exactly one" : `${String(value)} of them`;
  if (value.min === value.max) return `exactly ${String(value.min)}`;
  return `${String(value.min)} to ${String(value.max)}`;
}

/** The setting a cardinality is driven by, when it has one. */
export function drivenBy(count: Requirement["count"]): string | null {
  const value = count ?? 1;
  return typeof value === "number" ? null : (value.from ?? null);
}

export function bindingAt(
  added: AddedActivityRow,
  name: string,
  position: number,
): ConsoleBinding | null {
  return (
    added.bindings.find(
      (binding) => binding.requirement_name === name && binding.position === position,
    ) ?? null
  );
}

/** One entry per position, 1-indexed, holding whatever is bound there. */
export function positionsOf(
  added: AddedActivityRow,
  requirement: NamedRequirement,
): { position: number; binding: ConsoleBinding | null }[] {
  const total = countOf(added, requirement);
  const rows: { position: number; binding: ConsoleBinding | null }[] = [];
  for (let position = 1; position <= total; position += 1) {
    rows.push({ position, binding: bindingAt(added, requirement.name, position) });
  }
  return rows;
}

/** Bindings past the current cardinality. Never repaired, never dropped. */
export function strayBindings(
  added: AddedActivityRow,
  requirement: NamedRequirement,
): ConsoleBinding[] {
  const total = countOf(added, requirement);
  return added.bindings.filter(
    (binding) => binding.requirement_name === requirement.name && binding.position > total,
  );
}

/** Required positions with nothing bound. */
export function openPositions(
  added: AddedActivityRow,
  requirements: NamedRequirement[],
): { requirement: string; position: number; type: string }[] {
  const out: { requirement: string; position: number; type: string }[] = [];
  for (const requirement of requirements) {
    if (requirement.required === false) continue;
    for (const row of positionsOf(added, requirement)) {
      if (row.binding === null) {
        out.push({ requirement: requirement.name, position: row.position, type: requirement.type });
      }
    }
  }
  return out;
}
